import { useEffect, useState } from "react";
import { Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import { useProvider } from "../context/MenuProvider";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  LayersControl,
  LayerGroup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css";
import "leaflet-extra-markers/dist/js/leaflet.extra-markers.min.js";

// Ajusta mapa para mostrar posição do usuário
function AjustaMapa({ userPos, raioMetros = 50 }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !userPos || !Array.isArray(userPos) || userPos.length !== 2) return;

    const [lat, lng] = userPos;

    if (raioMetros) {
      const latDelta = raioMetros / 111320;
      const lngDelta = raioMetros / (111320 * Math.cos((lat * Math.PI) / 180));
      const bounds = L.latLngBounds([lat - latDelta, lng - lngDelta], [lat + latDelta, lng + lngDelta]);
      map.fitBounds(bounds, { padding: [10, 10] });
    } else {
      map.setView(userPos, 15);
    }
  }, [userPos, raioMetros, map]);

  return null;
}

// Ícones
const userIcon = L.ExtraMarkers.icon({
  icon: "bx-user bx-sm",
  markerColor: "red",
  shape: "circle",
  prefix: "bx",
});
const vendaIcon = L.ExtraMarkers.icon({
  icon: "bx-shopping-bag bx-sm",
  markerColor: "blue",
  shape: "circle",
  prefix: "bx",
});

// Componente para capturar base layer e overlays
function CapturaLayers() {
  const map = useMap();

  useEffect(() => {
    const handleBaseChange = (e) => localStorage.setItem("ultimoBaseLayer", e.name);
    const handleOverlayAdd = (e) => {
      const ativos = JSON.parse(localStorage.getItem("overlaysAtivos") || "[]");
      if (!ativos.includes(e.name)) ativos.push(e.name);
      localStorage.setItem("overlaysAtivos", JSON.stringify(ativos));
    };
    const handleOverlayRemove = (e) => {
      const ativos = JSON.parse(localStorage.getItem("overlaysAtivos") || "[]");
      localStorage.setItem("overlaysAtivos", JSON.stringify(ativos.filter((n) => n !== e.name)));
    };

    map.on("baselayerchange", handleBaseChange);
    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("baselayerchange", handleBaseChange);
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map]);

  return null;
}

export default function MeuMapa() {
  const [inicialPos, setInicialPos] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [raioMetros, setRaioMetros] = useState(null);
  const [cercaVirtual, setCercaVirtual] = useState(null);
  const [tipos, setTipos] = useState([]);
  const { salvarDados, buscarDadosPorId, excluirDadosPorId } = useProvider();

  const listaTipos = {
    vendas: "Vendas",
    clientes: "Clientes",
    locais: "Estabelecimentos",
    agenda: "Agendamentos",
  };

  // Marcadores originais
  const marcador1 = [
    { nome: "Marcador 1", lat: -23.1023, lng: -46.9121 },
    { nome: "Marcador 1", lat: -23.4532, lng: -46.7215 },
    { nome: "Marcador 1", lat: -23.7125, lng: -46.8654 },
    { nome: "Marcador 1", lat: -23.5678, lng: -46.7122 },
    { nome: "Marcador 1", lat: -23.2234, lng: -46.8011 },
    { nome: "Marcador 1", lat: -23.6450, lng: -46.9023 },
    { nome: "Marcador 1", lat: -23.7891, lng: -46.7545 },
    { nome: "Marcador 1", lat: -23.3120, lng: -46.9342 },
    { nome: "Marcador 1", lat: -23.8765, lng: -46.8231 },
    { nome: "Marcador 1", lat: -23.4210, lng: -46.7654 },
  ];

  const marcador2 = [
    { nome: "Marcador 2", lat: -23.5321, lng: -46.7012 },
    { nome: "Marcador 2", lat: -23.6543, lng: -46.8123 },
    { nome: "Marcador 2", lat: -23.7432, lng: -46.9321 },
    { nome: "Marcador 2", lat: -23.3210, lng: -46.7234 },
    { nome: "Marcador 2", lat: -23.4123, lng: -46.8654 },
    { nome: "Marcador 2", lat: -23.5674, lng: -46.8120 },
    { nome: "Marcador 2", lat: -23.7654, lng: -46.7432 },
    { nome: "Marcador 2", lat: -23.4321, lng: -46.8230 },
    { nome: "Marcador 2", lat: -23.6210, lng: -46.7541 },
    { nome: "Marcador 2", lat: -23.5432, lng: -46.9012 },
  ];

  const marcador3 = [
    { nome: "Marcador 3", lat: -23.3123, lng: -46.7124 },
    { nome: "Marcador 3", lat: -23.4321, lng: -46.8342 },
    { nome: "Marcador 3", lat: -23.5678, lng: -46.9123 },
    { nome: "Marcador 3", lat: -23.6543, lng: -46.7654 },
    { nome: "Marcador 3", lat: -23.7210, lng: -46.8234 },
    { nome: "Marcador 3", lat: -23.8123, lng: -46.7432 },
    { nome: "Marcador 3", lat: -23.4210, lng: -46.9012 },
    { nome: "Marcador 3", lat: -23.5432, lng: -46.7123 },
    { nome: "Marcador 3", lat: -23.6540, lng: -46.8345 },
    { nome: "Marcador 3", lat: -23.7321, lng: -46.8123 },
  ];

  const marcador4 = [
    { nome: "Marcador 4", lat: -23.3121, lng: -46.7345 },
    { nome: "Marcador 4", lat: -23.4320, lng: -46.8231 },
    { nome: "Marcador 4", lat: -23.5432, lng: -46.7543 },
    { nome: "Marcador 4", lat: -23.6543, lng: -46.9120 },
    { nome: "Marcador 4", lat: -23.7650, lng: -46.8234 },
    { nome: "Marcador 4", lat: -23.8761, lng: -46.7430 },
    { nome: "Marcador 4", lat: -23.3214, lng: -46.8121 },
    { nome: "Marcador 4", lat: -23.4325, lng: -46.9012 },
    { nome: "Marcador 4", lat: -23.5430, lng: -46.7650 },
    { nome: "Marcador 4", lat: -23.6542, lng: -46.8340 },
  ];

  // Recupera base e overlays salvos
  const ultimoBase = localStorage.getItem("ultimoBaseLayer") || "OpenStreetMap Padrão";
  const overlaysAtivos = JSON.parse(localStorage.getItem("overlaysAtivos") || "null") || ["Marcadores 1", "Marcadores 2", "Marcadores 3", "Marcadores 4", "Você", "Cerca Virtual"];
  
  // Geolocalização
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserPos([pos.coords.latitude, pos.coords.longitude]),
        (err) => alert("Erro ao buscar localização: " + err.message)
      );
    }
  }, []);

  useEffect(() => {
    let watchId;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => setUserPos([pos.coords.latitude, pos.coords.longitude]),
        (err) => alert("Erro ao buscar localização: " + err.message),
        { enableHighAccuracy: true, maximumAge: 10000 }
      );
    }
    return () => watchId && navigator.geolocation.clearWatch(watchId);
  }, []);

  // Buscar cerca virtual
  useEffect(() => {
    const buscarCerca = async () => {
      const response = await buscarDadosPorId("local_registros", "cerca");
      if (response) setCercaVirtual(response);
    };
    buscarCerca();
  }, []);
  
  useEffect(()=>{
    if(!inicialPos){
      setInicialPos(userPos);
    }
  },[userPos])

  const handleChange = (e) => {
    const values = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setTipos(values.includes("todos") ? ["todos"] : values);
  };

  const criarCerca = async () => {
    setRaioMetros("");
    setTipos([]);
    if (raioMetros || tipos?.length > 0){
      const nomesFormatados = (() => {
        if (!tipos) return "";
        const arr = tipos.map((t) => listaTipos[t]);
        if (arr.length === 1) return arr[0];
        return arr.slice(0, -1).join(", ") + " e " + arr.slice(-1);
      })();
      
      const cerca = {
        raioMetros: raioMetros,
        userPos: userPos,
        tipos: nomesFormatados,
        titulo: `Cerca virtual de ${
          raioMetros < 1000
            ? `${raioMetros||0} metro${raioMetros>1?"s":""}`
            : `${(raioMetros / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km`
        }`,
        obs: `${!raioMetros?`Exibindo ${nomesFormatados}`:`${tipos?.length > 0 ? `${nomesFormatados} no r` : "R"}aio de ${
          raioMetros < 1000
            ? `${raioMetros} metros`
            : `${(raioMetros / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km`
        }`}`,
        id: "cerca",
      };
      
      setCercaVirtual(cerca);
      await salvarDados("local_registros",cerca)
    } else {
      setCercaVirtual(null);
      await excluirDadosPorId("local_registros", "cerca");
    }
  }

  return (
    <div className="mapa">
      <main className="container">
        <Row className="mb-1">
          <Col xs={12}>
            <InputGroup>
              <Form.Control as="select" value={raioMetros || ""} onChange={(e) => setRaioMetros(e.target.value)}>
                <option value="">Selecione o raio</option>
                <option value={100}>100 metros</option>
                <option value={200}>200 metros</option>
                <option value={500}>500 metros</option>
                <option value={1000}>1 Km</option>
                <option value={1500}>1,5 Km</option>
                <option value={2000}>2 Km</option>
                <option value={3000}>3 Km</option>
                <option value={4000}>4 Km</option>
                <option value={5000}>5 Km</option>
                <option value={7000}>7 Km</option>
                <option value={10000}>10 Km</option>
              </Form.Control>
  
              <Form.Control as="select" multiple value={tipos} onChange={handleChange}>
                {Object.entries(listaTipos).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </Form.Control>
  
              <Button onClick={criarCerca}>Ok</Button>
            </InputGroup>
          </Col>
          <Col xs={12}>
            <div>{cercaVirtual?.obs}</div>
          </Col>
        </Row>
      </main>

      <MapContainer center={[-25.6, -60]} zoom={3} style={{ height: "100%", width: "100%" }}>
        <LayersControl position="topright">
          <CapturaLayers />

          {/* Base Layers */}
          <LayersControl.BaseLayer checked={ultimoBase === "OpenStreetMap Padrão"} name="OpenStreetMap Padrão">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked={ultimoBase === "CartoDB Positron"} name="CartoDB Positron">
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution="© OpenStreetMap, © CartoDB" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked={ultimoBase === "CartoDB Dark Matter"} name="CartoDB Dark Matter">
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="© OpenStreetMap, © CartoDB" />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked={ultimoBase === "OSM Humanitarian"} name="OSM Humanitarian">
            <TileLayer url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
          </LayersControl.BaseLayer>

          {/* Overlays */}
          <LayersControl.Overlay checked name="Marcadores 1">
            <LayerGroup>{marcador1.map((p, i) => <Marker key={i} position={[p.lat, p.lng]} icon={vendaIcon}><Popup>{p.nome}</Popup></Marker>)}</LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Marcadores 2">
            <LayerGroup>{marcador2.map((p, i) => <Marker key={i} position={[p.lat, p.lng]} icon={vendaIcon}><Popup>{p.nome}</Popup></Marker>)}</LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Marcadores 3">
            <LayerGroup>{marcador3.map((p, i) => <Marker key={i} position={[p.lat, p.lng]} icon={vendaIcon}><Popup>{p.nome}</Popup></Marker>)}</LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name="Marcadores 4">
            <LayerGroup>{marcador4.map((p, i) => <Marker key={i} position={[p.lat, p.lng]} icon={vendaIcon}><Popup>{p.nome}</Popup></Marker>)}</LayerGroup>
          </LayersControl.Overlay>

          {userPos && (
            <LayersControl.Overlay checked name="Você">
              <LayerGroup>
                <Marker position={userPos} icon={userIcon}><Popup>Você está aqui</Popup></Marker>
                {raioMetros && <Circle center={userPos} radius={raioMetros} pathOptions={{ color: "orange", fillColor: "orange", fillOpacity: 0.001 }} />}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {cercaVirtual?.raioMetros && (
            <LayersControl.Overlay checked name="Cerca Virtual">
              <LayerGroup>
                <Circle center={cercaVirtual.userPos} radius={cercaVirtual.raioMetros} pathOptions={{ color: "red", fillColor: "red", fillOpacity: 0.001 }} />
              </LayerGroup>
            </LayersControl.Overlay>
          )}

        </LayersControl>

        <AjustaMapa userPos={inicialPos || cercaVirtual?.userPos} raioMetros={cercaVirtual?.raioMetros} />
      </MapContainer>
    </div>
  );
}